import { CrudModel } from "../plugins/Cruxtify/CrudModel";
import {
  BooleanField,
  DateField,
  DateTimeField,
  NumberField,
  RelationshipField,
  SelectField,
  StringField,
} from "../plugins/Cruxtify/field-types";
import {
  BooleanProperty,
  DateProperty,
  DateTimeProperty,
  NumberProperty,
  RelationshipProperty,
  StringProperty,
} from "../plugins/Cruxtify/property-types";
import {
  UserPermission,
  UserPermissions,
  UserRole,
} from "../plugins/Cruxtify/UserPermission";

/**
 * One job-board position for a contract: the contract's stage and crew for a
 * single trade. The TV board and phone remote are built from these rows. The
 * contract record's Job Board tab lists them read-only; the ContractJobTypes
 * header control adds/removes rows and the remote edits stage and crew
 * (20260925-1220-job-board).
 */
export class ContractTradeStage extends CrudModel {
  protected static _typeLabel = "Board Stage";
  protected static asProperty = "contract_trade_stage";
  public static api = {
    path: "contract-trade-stages",
  };
  protected static routeBase = "contract-trade-stages";
  protected userIdField = [];
  protected static userPermissions = new UserPermissions(UserPermission.Read);

  protected static propertyDefinitions = [
    {
      type: NumberProperty,
      opts: {
        name: "id",
        label: "ID",
        userPermissions: UserPermission.Hidden,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "deleted_at",
        label: "Deleted At",
        userPermissions: UserPermission.Read,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "updated_at",
        label: "Updated At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "contract",
        label: "Contract",
        relatedModel: "Contract",
        rules: ["required"],
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "trade",
        label: "Trade",
        rules: ["required"],
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "stage",
        label: "Board Stage",
        rules: ["required"],
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "crew",
        label: "Crew",
        relatedModel: "Crew",
      },
    },
    {
      type: BooleanProperty,
      opts: {
        name: "on_hold",
        label: "On Hold",
        default: false,
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "on_hold_reason",
        label: "Hold Reason",
      },
    },
    {
      type: DateProperty,
      opts: {
        name: "started_at",
        label: "Started",
      },
    },
  ];

  protected static fieldDefinitions = [
    { type: NumberField, opts: { property: "id" } },
    { type: DateTimeField, opts: { property: "updated_at" } },
    { type: DateTimeField, opts: { property: "deleted_at" } },
    { type: RelationshipField, opts: { property: "contract" } },
    {
      type: SelectField,
      opts: {
        property: "trade",
        // Mirrors config/job_board.php's job_types keys and labels, as
        // Crew.ts's type options already do.
        options: { roof: "Roof", gutters: "Gutters", siding: "Siding", repairs: "Repairs" },
        md: 6,
      },
    },
    {
      type: SelectField,
      opts: {
        property: "stage",
        // Same values and labels as the board's columns (JobBoardQuickEdit STAGE_OPTIONS).
        options: {
          to_be_loaded: "To Be Loaded",
          loaded: "Loaded",
          next_job: "Next Job",
          job_on: "Job On",
          repairs: "Repairs",
          complete: "Complete",
        },
        md: 6,
      },
    },
    { type: RelationshipField, opts: { property: "crew", md: 6 } },
    { type: DateField, opts: { property: "started_at", md: 6 } },
    { type: BooleanField, opts: { property: "on_hold", md: 6 } },
    {
      type: StringField,
      opts: {
        property: "on_hold_reason",
        md: 6,
        conditional: { property: "on_hold", value: true },
      },
    },
  ];

  protected static collectionLayoutDefinitions = [
    {
      id: "asRelationship",
      columnFields: [
        "trade",
        "stage",
        "crew",
        "on_hold",
        "on_hold_reason",
        "started_at",
      ],
      useModal: true,
      defaultSort: [
        {
          field: "trade",
          order: "desc",
        },
      ],
    },
  ];

  protected static modelLayoutDefinitions = [
    {
      id: "Crudform",
      fieldSets: [
        {
          id: "0",
          fields: [
            "trade",
            "stage",
            "crew",
            "started_at",
            "on_hold",
            "on_hold_reason",
          ],
        },
      ],
    },
  ];
}
